/* Pagina de produs Armonia — comportamentul din mockup (design-preview/produs.html),
 * legat la datele reale din Shopify.
 *
 * Ce face:
 *  - galeria: thumbnail-uri + lupă la hover (doar când poza are pixeli de arătat);
 *  - swatch-urile: țin în sincron select-ul `name="id"`, prețul, poza, disponibilitatea;
 *  - configuratorul de piese: total live și adăugarea mai multor variante odată
 *    (/cart/add.js cu items[]), pentru produsele-set.
 *
 * Fără JavaScript pagina rămâne funcțională: select-ul nativ de variante e vizibil
 * (clasa `js` de pe rădăcină îl ascunde) și formularul face un POST normal la /cart/add.
 */

(() => {
  'use strict';

  const REDUS = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ bani */
  // Shopify nu expune un formatter în temă; îl construim din shop.money_format.
  function grupeaza(bani, zecimale, sepMii, sepZecimal) {
    const n = (bani / 100).toFixed(zecimale);
    const bucati = n.split('.');
    const intreg = bucati[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMii);
    return bucati[1] ? intreg + sepZecimal + bucati[1] : intreg;
  }

  function formateazaBani(bani, format) {
    const f = (format || '{{amount_with_comma_separator}} lei').replace(/<[^>]+>/g, '');
    const potrivire = f.match(/\{\{\s*(\w+)\s*\}\}/);
    const tip = potrivire ? potrivire[1] : 'amount';
    let valoare;
    switch (tip) {
      case 'amount_no_decimals': valoare = grupeaza(bani, 0, ',', '.'); break;
      case 'amount_with_comma_separator': valoare = grupeaza(bani, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': valoare = grupeaza(bani, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': valoare = grupeaza(bani, 2, "'", '.'); break;
      case 'amount_with_space_separator': valoare = grupeaza(bani, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': valoare = grupeaza(bani, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': valoare = grupeaza(bani, 2, ' ', '.'); break;
      default: valoare = grupeaza(bani, 2, ',', '.');
    }
    return f.replace(/\{\{\s*\w+\s*\}\}/, valoare).trim();
  }

  // 1 piesă / 2–19 piese / 20 de piese — acordul din română
  function pluralPiese(n) {
    if (n === 1) return '1 piesă selectată';
    if (n < 20) return n + ' piese selectate';
    return n + ' de piese selectate';
  }

  function pluralSet(n) {
    if (n === 1) return 'Set complet (1 piesă)';
    if (n < 20) return 'Set complet (' + n + ' piese)';
    return 'Set complet (' + n + ' de piese)';
  }

  class ProdusArmonia {
    constructor(radacina) {
      this.el = radacina;
      this.t = radacina.dataset;
      this.formular = radacina.querySelector('.pform');
      this.selectVarianta = radacina.querySelector('[data-varianta-select]');
      this.variante = this.citesteVariante();

      this.butonAdd = radacina.querySelector('[data-add]');
      this.butonAddText = radacina.querySelector('[data-add-text]');
      this.butonSticky = radacina.querySelector('[data-add-sticky]');
      this.eroare = radacina.querySelector('[data-eroare]');

      this.initGalerie();
      this.initDescriere();
      this.initCantitati();
      // piesele înaintea swatch-urilor: pe produsele-set ele conduc butonul de coș
      this.initPiese();
      // bundle-ul înaintea swatch-urilor: `actualizeazaVarianta` îl recalculează
      this.initBundle();
      this.initSwatchuri();

      if (this.formular) {
        this.formular.addEventListener('submit', (e) => this.laTrimitere(e));
      }
    }

    citesteVariante() {
      const nod = this.el.querySelector('[data-armonia-variante]');
      if (!nod) return [];
      try {
        return JSON.parse(nod.textContent);
      } catch (e) {
        console.error('Armonia: datele de variante nu se pot citi', e);
        return [];
      }
    }

    /* --------------------------------------------------------------- galerie */
    initGalerie() {
      this.gmain = this.el.querySelector('[data-gmain]');
      this.gimg = this.el.querySelector('[data-gmain-img]');
      this.thumbs = Array.from(this.el.querySelectorAll('[data-thumb]'));
      if (!this.gmain || !this.gimg) return;

      this.thumbs.forEach((t) => {
        t.addEventListener('click', () => this.aratăThumb(t));
      });

      if (REDUS.matches) return;

      if (this.gimg.complete) this.verificaZoom();
      else this.gimg.addEventListener('load', () => this.verificaZoom(), { once: true });

      this.gmain.addEventListener('mousemove', (e) => {
        if (!this.gmain.classList.contains('zoomable')) return;
        const c = this.gmain.getBoundingClientRect();
        this.gmain.style.setProperty('--zx', ((e.clientX - c.left) / c.width) * 100 + '%');
        this.gmain.style.setProperty('--zy', ((e.clientY - c.top) / c.height) * 100 + '%');
        this.gmain.classList.add('zoomed');
      });
      this.gmain.addEventListener('mouseleave', () => this.gmain.classList.remove('zoomed'));
    }

    // lupa are sens doar dacă fișierul are mai mulți pixeli decât caseta
    verificaZoom() {
      if (REDUS.matches || !this.gimg || !this.gmain) return;
      const merita =
        window.matchMedia('(pointer: fine)').matches &&
        this.gimg.naturalWidth > this.gmain.clientWidth * 1.2;
      this.gmain.classList.toggle('zoomable', merita);
    }

    aratăThumb(thumb) {
      if (!thumb || !this.gimg) return;
      this.thumbs.forEach((t) => {
        const activ = t === thumb;
        t.classList.toggle('on', activ);
        if (activ) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });

      const src = thumb.dataset.src;
      if (!src || this.gimg.getAttribute('src') === src) return;

      this.gmain.classList.remove('zoomed', 'zoomable');
      this.gmain.classList.add('swapping');
      const schimba = () => {
        this.gimg.srcset = thumb.dataset.srcset || '';
        this.gimg.src = src;
        this.gimg.removeAttribute('width');
        this.gimg.removeAttribute('height');
        this.gimg.addEventListener(
          'load',
          () => {
            this.gmain.classList.remove('swapping');
            this.verificaZoom();
          },
          { once: true }
        );
        // dacă poza e deja în cache, `load` poate să nu mai vină la timp
        setTimeout(() => this.gmain.classList.remove('swapping'), 400);
      };
      REDUS.matches ? schimba() : setTimeout(schimba, 180);
    }

    aratăMedia(idMedia) {
      if (!idMedia) return;
      const thumb = this.thumbs.find((t) => t.dataset.media === String(idMedia));
      if (thumb) this.aratăThumb(thumb);
    }

    /* ------------------------------------------------------------- cantități */
    initCantitati() {
      this.el.addEventListener('click', (e) => {
        const buton = e.target.closest('[data-qty-step]');
        if (!buton) return;
        const camp = buton.parentElement.querySelector('.pqin');
        if (!camp) return;
        const min = parseInt(camp.min, 10) || 1;
        const max = parseInt(camp.max, 10) || 999;
        const acum = parseInt(camp.value, 10) || min;
        camp.value = Math.min(max, Math.max(min, acum + parseInt(buton.dataset.qtyStep, 10)));
        camp.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /* ------------------------------------------------------------- variante */
    initSwatchuri() {
      this.swatchuri = Array.from(this.el.querySelectorAll('[data-sw]'));
      if (!this.swatchuri.length) {
        this.actualizeazaButon(this.variantaDinSelect());
        return;
      }

      this.nrOptiuni = this.swatchuri.reduce((m, s) => Math.max(m, parseInt(s.dataset.opt, 10) + 1), 0);
      this.selectie = new Array(this.nrOptiuni).fill(null);
      this.swatchuri.forEach((s) => {
        const radio = s.querySelector('.swin');
        if (radio && radio.checked) this.selectie[parseInt(s.dataset.opt, 10)] = s.dataset.val;
        if (radio) radio.addEventListener('change', () => this.alegeSwatch(s));
      });

      this.actualizeazaVarianta(false);
    }

    alegeSwatch(swatch) {
      const grup = parseInt(swatch.dataset.opt, 10);
      this.selectie[grup] = swatch.dataset.val;
      this.swatchuri
        .filter((s) => parseInt(s.dataset.opt, 10) === grup)
        .forEach((s) => s.classList.toggle('on', s === swatch));
      this.actualizeazaVarianta(true);
    }

    gasesteVarianta(selectie) {
      return this.variante.find((v) => selectie.every((val, i) => val === null || v.optiuni[i] === val));
    }

    actualizeazaVarianta(schimbaPoza) {
      const varianta = this.gasesteVarianta(this.selectie);

      // etichetele de lângă numele opțiunii
      this.el.querySelectorAll('[data-opt-eticheta]').forEach((b) => {
        const i = parseInt(b.dataset.optEticheta, 10);
        const sw = this.swatchuri.find((s) => parseInt(s.dataset.opt, 10) === i && s.classList.contains('on'));
        b.textContent = sw ? ' ' + sw.dataset.eticheta : '';
      });

      // valorile care nu formează nicio variantă disponibilă
      this.swatchuri.forEach((s) => {
        const i = parseInt(s.dataset.opt, 10);
        const proba = this.selectie.slice();
        proba[i] = s.dataset.val;
        const gasita = this.variante.find(
          (v) => v.disponibila && proba.every((val, j) => val === null || v.optiuni[j] === val)
        );
        s.classList.toggle('soldout', !gasita);
      });

      // preț
      const pret = this.el.querySelector('[data-pret-val]');
      const pretVechi = this.el.querySelector('[data-pret-vechi]');
      if (pret) {
        if (varianta) {
          // „de la X" (randat de Liquid) devine prețul exact de îndată ce
          // configurația e completă — ca în mockup
          const completa = this.selectie.every((v) => v !== null);
          const sufix = completa && this.t.tConfigurat ? ' ' + this.t.tConfigurat : '';
          pret.textContent = varianta.pret + sufix;
        } else {
          pret.textContent = this.t.tCombinatie;
        }
      }
      if (pretVechi) {
        const arata = varianta && varianta.compara > varianta.pret_brut;
        pretVechi.hidden = !arata;
        if (arata) pretVechi.textContent = varianta.compara_afisat;
      }

      // rezumatul configurației + linkul de WhatsApp
      const rezumat = this.etichetaConfiguratie();
      const confsum = this.el.querySelector('[data-confsum]');
      if (confsum) confsum.textContent = rezumat || '—';
      this.actualizeazaWhatsApp(rezumat);

      // varianta trimisă în coș
      if (varianta && this.selectVarianta) this.selectVarianta.value = varianta.id;
      if (varianta && schimbaPoza && varianta.media) this.aratăMedia(varianta.media);

      this.actualizeazaButon(varianta);
      // patul s-a reconfigurat → totalul setului se recalculează pe prețul nou
      this.recalculeazaBundle();
    }

    etichetaConfiguratie() {
      if (!this.swatchuri || !this.swatchuri.length) return '';
      return this.swatchuri
        .filter((s) => s.classList.contains('on'))
        .sort((a, b) => a.dataset.opt - b.dataset.opt)
        .map((s) => s.dataset.eticheta)
        .join(' · ');
    }

    actualizeazaWhatsApp(rezumat) {
      const numar = this.t.waNumar;
      if (!numar) return;
      let text = (this.t.waMesaj || '') + ' ' + (this.t.produs || '');
      if (rezumat) text += ' — ' + rezumat;
      const href = 'https://wa.me/' + numar + '?text=' + encodeURIComponent(text.trim() + '.');
      this.el.querySelectorAll('[data-wa]').forEach((a) => (a.href = href));
    }

    variantaDinSelect() {
      if (!this.selectVarianta) return null;
      const id = parseInt(this.selectVarianta.value, 10);
      return this.variante.find((v) => v.id === id) || null;
    }

    // Cele două butoane de coș (cel din formular și cel din bara lipită de pe mobil)
    // trebuie să arate ACEEAȘI stare: `laTrimitere` refuză trimiterea când e dezactivat,
    // deci un buton mobil care pare activ ar înghiți tap-ul fără niciun feedback.
    seteazaButoane(eticheta, dezactivat) {
      [this.butonAdd, this.butonSticky].forEach((b) => {
        if (b) b.setAttribute('aria-disabled', dezactivat ? 'true' : 'false');
      });
      if (this.butonAddText) this.butonAddText.textContent = eticheta;
      if (this.butonSticky) this.butonSticky.textContent = eticheta;
    }

    actualizeazaButon(varianta) {
      if (!this.butonAdd) return;
      // pe produsele-set butonul e condus de configuratorul de piese
      if (this.piese && this.piese.length) return;

      const indisponibil = varianta ? !varianta.disponibila : true;
      this.seteazaButoane(indisponibil ? this.t.tIndisponibil : this.t.tAdd, indisponibil);
    }

    /* ---------------------------------------------------- configurator piese */
    initPiese() {
      this.piese = Array.from(this.el.querySelectorAll('[data-piesa]')).filter((c) =>
        c.querySelector('[data-piesa-bifa]')
      );
      if (!this.piese.length) return;

      this.piesaTotal = this.el.querySelector('[data-piese-total]');
      this.piesaNumar = this.el.querySelector('[data-piese-numar]');

      this.el.addEventListener('change', (e) => {
        if (e.target.closest('[data-piesa]')) this.recalculeazaPiese();
      });
      this.recalculeazaPiese();
    }

    recalculeazaPiese() {
      let total = 0;
      let bucati = 0;

      this.piese.forEach((card) => {
        const bifa = card.querySelector('[data-piesa-bifa]');
        const camp = card.querySelector('[data-piesa-buc]');
        const min = parseInt(camp.min, 10) || 1;
        const max = parseInt(camp.max, 10) || 20;
        const cantitate = Math.min(max, Math.max(min, parseInt(camp.value, 10) || min));
        if (String(cantitate) !== camp.value) camp.value = cantitate;

        card.classList.toggle('off', !bifa.checked);
        if (bifa.checked) {
          total += (parseInt(card.dataset.pret, 10) || 0) * cantitate;
          bucati += cantitate;
        }
      });

      if (this.piesaTotal) this.piesaTotal.textContent = bucati ? formateazaBani(total, this.t.moneda) : '—';
      if (this.piesaNumar) this.piesaNumar.textContent = bucati ? pluralPiese(bucati) : this.t.tNicio;

      this.seteazaButoane(bucati ? this.t.tAddPiese : this.t.tNimic, !bucati);
    }

    /* ------------------------------------------------------------ descriere */
    initDescriere() {
      const zona = this.el.querySelector('[data-descriere]');
      if (!zona) return;
      const text = zona.querySelector('[data-descriere-text]');
      const buton = zona.querySelector('[data-descriere-buton]');
      const eticheta = zona.querySelector('[data-descriere-eticheta]');
      if (!text || !buton) return;

      // Măsurătoarea se face pe descrierea STRÂNSĂ: `scrollHeight` peste `clientHeight`
      // înseamnă că se pierde text. Pragul e de DOUĂ rânduri, nu de câțiva pixeli — un
      // buton care descoperă un rând și jumătate (Dormitor Hera: 231px față de 193)
      // costă un clic și nu arată nimic. Sub prag, descrierea rămâne întreagă.
      const masoara = () => {
        zona.classList.add('strans');
        const rand = parseFloat(getComputedStyle(text).lineHeight) || 24;
        const depaseste = text.scrollHeight - text.clientHeight > rand * 2;
        buton.hidden = !depaseste;
        if (!depaseste) zona.classList.remove('strans');
      };

      masoara();
      // fonturile serif se încarcă după primul layout și schimbă înălțimea textului
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (buton.getAttribute('aria-expanded') !== 'true') masoara();
        });
      }

      buton.addEventListener('click', () => {
        const strans = zona.classList.toggle('strans');
        buton.setAttribute('aria-expanded', strans ? 'false' : 'true');
        if (eticheta) eticheta.textContent = strans ? buton.dataset.tMaiMult : buton.dataset.tMaiPutin;
        // la restrângere, textul de deasupra poate sări sub fold — readucem butonul în cadru
        if (strans) {
          const sus = zona.getBoundingClientRect().top;
          if (sus < 0) zona.scrollIntoView({ behavior: REDUS.matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
    }

    /* --------------------------------------- cumpărate frecvent împreună */
    initBundle() {
      this.bundle = this.el.querySelector('[data-bundle]');
      if (!this.bundle) return;

      this.bundleAddons = Array.from(this.bundle.querySelectorAll('[data-bundle-addon]'));
      this.bundlePct = parseInt(this.bundle.dataset.pct, 10) || 0;
      // prețul de plecare al patului; `actualizeazaVarianta` îl rescrie la fiecare schimbare
      this.bundlePretPat = parseInt(this.bundle.dataset.pretPat, 10) || 0;

      this.bundleAddons.forEach((c) => c.addEventListener('change', () => this.recalculeazaBundle()));

      const buton = this.bundle.querySelector('[data-bundle-add]');
      if (buton) buton.addEventListener('click', () => this.adaugaBundle());

      this.recalculeazaBundle();
    }

    recalculeazaBundle() {
      if (!this.bundle) return;

      const varianta = this.variantaDinSelect();
      if (varianta && typeof varianta.pret_brut === 'number') this.bundlePretPat = varianta.pret_brut;

      const bifate = this.bundleAddons.filter((c) => c.checked);
      const bucati = 1 + bifate.length;
      const numar = this.bundle.querySelector('[data-bundle-numar]');
      if (numar) numar.textContent = pluralSet(bucati);

      const pretPat = this.bundle.querySelector('[data-bundle-pat]');
      const total = this.bundle.querySelector('[data-bundle-total]');
      const subtotal = this.bundle.querySelector('[data-bundle-subtotal]');
      const economie = this.bundle.querySelector('[data-bundle-economie]');

      this.actualizeazaWhatsAppBundle(bifate);

      // patul fără preț rămâne „la cerere": un total pornit de la 0 ar afișa doar
      // add-on-urile și ar arăta ca un preț de pat ridicol de mic
      if (!this.bundlePretPat) {
        if (subtotal) subtotal.hidden = true;
        if (economie) economie.hidden = true;
        return;
      }

      const totalAddon = bifate.reduce((s, c) => s + (parseInt(c.dataset.pret, 10) || 0), 0);
      const economisit = Math.round((totalAddon * this.bundlePct) / 100);
      const brut = this.bundlePretPat + totalAddon;

      if (pretPat) pretPat.textContent = formateazaBani(this.bundlePretPat, this.t.moneda);
      if (total) total.textContent = formateazaBani(brut - economisit, this.t.moneda);

      if (economisit > 0) {
        if (subtotal) {
          subtotal.textContent = formateazaBani(brut, this.t.moneda);
          subtotal.hidden = false;
        }
        if (economie) {
          economie.textContent =
            'Economisești ' + formateazaBani(economisit, this.t.moneda) +
            ' (−' + this.bundlePct + '% la piesele de set)';
          economie.hidden = false;
        }
      } else {
        if (subtotal) subtotal.hidden = true;
        if (economie) economie.hidden = true;
      }
    }

    actualizeazaWhatsAppBundle(bifate) {
      const link = this.bundle.querySelector('[data-bundle-wa]');
      const numar = this.t.waNumar;
      if (!link || !numar) return;
      let text = (this.t.waMesaj || '') + ' ' + (this.bundle.dataset.nume || '');
      if (bifate.length) text += ' + ' + bifate.map((c) => c.dataset.nume).join(' + ');
      link.href = 'https://wa.me/' + numar + '?text=' + encodeURIComponent(text.trim() + '.');
    }

    adaugaBundle() {
      const varianta = this.variantaDinSelect();
      if (!varianta) {
        this.aratăEroareBundle(this.t.tEroare);
        return;
      }
      const articole = [{ id: varianta.id, quantity: 1 }].concat(
        this.bundleAddons
          .filter((c) => c.checked)
          .map((c) => ({ id: parseInt(c.dataset.varianta, 10), quantity: 1 }))
      );
      this.trimiteLaCos(articole, this.bundle.querySelector('[data-bundle-add]'),
                       (m) => this.aratăEroareBundle(m));
    }

    aratăEroareBundle(mesaj) {
      const el = this.bundle && this.bundle.querySelector('[data-bundle-eroare]');
      if (!el) return;
      el.textContent = mesaj || '';
      el.hidden = !mesaj;
    }

    /* ------------------------------------------------------------- adăugare */
    articoleDeAdaugat() {
      if (this.piese && this.piese.length) {
        return this.piese
          .filter((card) => card.querySelector('[data-piesa-bifa]').checked)
          .map((card) => ({
            id: parseInt(card.dataset.varianta, 10),
            quantity: parseInt(card.querySelector('[data-piesa-buc]').value, 10) || 1,
          }));
      }

      const id = parseInt(this.selectVarianta && this.selectVarianta.value, 10);
      if (!id) return [];
      const camp = this.formular.querySelector('[name="quantity"]');
      return [{ id: id, quantity: parseInt(camp && camp.value, 10) || 1 }];
    }

    laTrimitere(e) {
      e.preventDefault();
      if (this.butonAdd && this.butonAdd.getAttribute('aria-disabled') === 'true') return;

      const articole = this.articoleDeAdaugat();
      if (!articole.length) {
        // ex. select-ul de variante n-a putut fi sincronizat — mai bine un mesaj
        // vizibil decât un buton care nu face nimic
        this.aratăEroare(this.t.tEroare);
        return;
      }

      this.aratăEroare('');
      this.trimiteLaCos(articole, null, (m) => this.aratăEroare(m));
    }

    /* Un singur drum spre coș pentru ambele butoane (formular și „adaugă setul"):
     * aceeași gestiune de eroare, aceeași protecție la dublu-tap, aceeași redirecție. */
    trimiteLaCos(articole, butonExtra, aratăEroare) {
      // `.loading` taie pointer-events și pe bara lipită, altfel pe mobil (unde bara
      // e singurul CTA vizibil) un al doilea tap ar trimite încă o dată coșul
      const butoane = [this.butonAdd, this.butonSticky, butonExtra].filter(Boolean);
      // o adăugare deja în curs: `.loading` oprește doar mouse-ul și tap-ul, nu și Enter-ul
      if (butoane.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      // Starea se și citește, nu doar se vede: „Se adaugă…" + aria-busy. Eticheta butonului mare
      // stă în `[data-add-text]`; celelalte două au text simplu.
      const eticheta = (b) => (b === this.butonAdd && this.butonAddText) || b;
      const vechi = butoane.map((b) => eticheta(b).textContent);
      const inLucru = (pornit) => butoane.forEach((b, i) => {
        b.classList.toggle('loading', pornit);
        b.setAttribute('aria-busy', pornit ? 'true' : 'false');
        eticheta(b).textContent = pornit ? (this.t.tSeAdauga || 'Se adaugă…') : vechi[i];
      });
      // la succes pagina pleacă spre coș: starea rămâne „Se adaugă…" până se încarcă,
      // altfel eticheta veche ar reapărea o clipă înainte de navigare
      let pleaca = false;
      inLucru(true);

      const ruta = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(ruta + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: articole }),
      })
        .then((r) => r.json())
        .then((raspuns) => {
          if (raspuns.status && raspuns.status !== 200) {
            aratăEroare(raspuns.description || raspuns.message || this.t.tEroare);
            return;
          }
          // Coșul e pagină (settings: cart_type „notification"), iar numărul din
          // headerul Armonia se randează server-side — deci mergem la coș, ca
          // starea afișată să fie mereu cea reală.
          pleaca = true;
          // „Înapoi" din coș readuce pagina din bfcache, cu butonul încă în „Se adaugă…"
          window.addEventListener('pageshow', (ev) => { if (ev.persisted) inLucru(false); }, { once: true });
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        })
        .catch(() => aratăEroare(this.t.tEroare))
        .finally(() => { if (!pleaca) inLucru(false); });
    }

    aratăEroare(mesaj) {
      if (!this.eroare) return;
      this.eroare.textContent = mesaj || '';
      this.eroare.hidden = !mesaj;
    }
  }

  function porneste(zona) {
    (zona || document).querySelectorAll('[data-armonia-produs]').forEach((el) => {
      if (el.dataset.pornit) return;
      el.dataset.pornit = '1';
      new ProdusArmonia(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => porneste());
  } else {
    porneste();
  }
  // theme editor: secțiunea se re-randează fără reload
  document.addEventListener('shopify:section:load', (e) => porneste(e.target));
})();
